/**
 * #3038 plan-review navigation wiring.
 *
 * Extracts the plan-review auto-navigation policy — the forward gate and
 * the return leg — out of Workspace.tsx into one hook so the wiring (the
 * two effects, the two refs, their dependency arrays, and the order they
 * run in) is a single named unit that both the renderer and the
 * integration test exercise. The pure DECISIONS live in
 * `plan-build-handoff.ts` (`shouldAutoNavigateToPlanReview` /
 * `shouldReturnToBuildFromPlan`); this hook owns the React plumbing they
 * sit inside.
 *
 * See `Workspace.plan-review-navigation.wiring.test.tsx` for the
 * component-level pin, and `plan-build-handoff.test.ts` for the helpers.
 */

import { useEffect, useRef } from 'react';
import {
  planReviewNavigationEligible,
  shouldAutoNavigateToPlanReview,
  shouldReturnToBuildFromPlan,
} from './plan-build-handoff';
import type { WorkspaceRunKind } from './run-state';
import type { WorkspaceMode } from './workspace-view-types';

// Sentinel so the per-game reset below also fires on the very FIRST render:
// initial mount is "switch-in from nowhere". Distinct from every possible gameId
// (string, null, or undefined), so `navGameIdRef.current !== gameId` is true on
// mount regardless of what the first gameId is.
const UNVISITED_GAME = Symbol('plan-review-nav:unvisited-game');

export function usePlanReviewNavigation(args: {
  /**
   * Workspace identity. Workspace is NOT remounted across /workspace/:gameId
   * route changes, so the per-game refs below must be reset when this changes
   * — otherwise game A's outstanding auto-nav / eligibility leaks into game B
   * (Codex R1). Same idiom as Workspace's initialisedModeForGameRef.
   */
  gameId: string | null | undefined;
  /** `flags.newUi` — the whole policy is inert in the legacy shell. */
  newUiEnabled: boolean;
  /** Design-tab surface is the plan-review home when this flag is on. */
  designWorkspaceEnabled: boolean;
  planHandoffVisible: boolean;
  runStateKind: WorkspaceRunKind;
  /** `autoBuildState != null` — an auto-proceeding build vetoes navigation. */
  autoBuildArmed: boolean;
  workspaceMode: WorkspaceMode;
  setWorkspaceMode: (mode: WorkspaceMode) => void;
  /** Bump the Design-tab Build-Plan focus nonce (design surface only). */
  bumpBuildPlanFocusNonce: () => void;
}): void {
  const {
    gameId,
    newUiEnabled,
    designWorkspaceEnabled,
    planHandoffVisible,
    runStateKind,
    autoBuildArmed,
    workspaceMode,
    setWorkspaceMode,
    bumpBuildPlanFocusNonce,
  } = args;

  // Eligibility is the full gate condition. Its two feeder signals land on
  // different renders (planHandoffVisible ← interview-status;
  // waiting-for-plan-review ← foundry snapshot), so the forward gate keys off
  // the eligibility rising edge — see shouldAutoNavigateToPlanReview.
  const eligibleNow = planReviewNavigationEligible({
    planHandoffVisible,
    runStateKind,
    autoBuildArmed,
  });

  // Seeded FALSE (not eligibleNow): the eligibility baseline this game's forward
  // gate has already acted on. The reset below rebases it on the first render and
  // on every game switch, so mount-into-an-open-gate fires the gate exactly like
  // switch-in (Codex R2).
  const previousEligibleRef = useRef(false);
  // #3038 return leg: true ONLY while a forward-gate AUTO-navigation to the plan
  // surface is outstanding. Guards the return so a MANUAL navigation to the plan
  // during a running build is never yanked. Consumed when the review resolves.
  const planViewAutoSetRef = useRef(false);
  // Codex R1/R2: the game these refs describe. Starts UNVISITED so the reset runs
  // on mount too. Workspace persists across in-place game switches, so the refs
  // must be reset when this changes.
  const navGameIdRef = useRef<string | null | undefined | typeof UNVISITED_GAME>(UNVISITED_GAME);
  // Codex R7: was the hook ENABLED on the previous run? Lets every disabled->enabled
  // flip be treated as a fresh entry epoch, not just the first enable.
  const prevEnabledRef = useRef(false);

  useEffect(() => {
    // Codex R6: while disabled (newUi off, or the flag IPC still in flight) the
    // hook stays inert w.r.t. the observation refs — it never reads/writes
    // previousEligible / planViewAutoSet / navGameId — so a gate that resolves
    // BEFORE flags load cannot consume the eligibility edge. We DO record the
    // disable in prevEnabledRef (a control flag, not an observation ref, so no
    // edge is consumed) so the NEXT enable is detected as a new epoch.
    if (!newUiEnabled) {
      prevEnabledRef.current = false;
      return;
    }
    // Codex R7: enable-is-entry for EVERY false->true flip, not only the first.
    // A flag flap (enable -> disable while the review resolves out of
    // waiting-for-plan-review -> re-enable) would otherwise leave planViewAutoSet
    // stale from the prior epoch — the return leg is inert while disabled so it
    // never consumed it — and yank the user to chat on re-enable. So any enable
    // epoch OR a gameId change (mount / switch) runs the same per-game reset.
    const enteredEnabledEpoch = !prevEnabledRef.current;
    prevEnabledRef.current = true;
    if (enteredEnabledEpoch || navGameIdRef.current !== gameId) {
      // Codex R1/R2: new game for this persistent component instance — which on
      // the FIRST render means initial mount ("switch-in from nowhere"), and
      // thereafter an in-place game switch (Workspace is not remounted across
      // /workspace/:gameId changes). One code path for all three: clear the
      // previous game's leftovers so its stale auto-nav can't yank this game and
      // its stale eligibility can't drive it, then seed previousEligible FALSE.
      //
      // Reset-to-FALSE (team-lead ruling under eligibility semantics): entering a
      // game whose build is genuinely BLOCKED on a plan-review gate SHOULD
      // auto-open the plan — the forward gate's whole purpose, equally true
      // whether the gate opened while watching, before arrival, or on a reload /
      // deep-link — and that entry nav arms this game's own return leg. Cannot
      // phantom-navigate: eligibility already excludes a merely-visible-but-
      // not-gated plan (eligibleNow is false there).
      navGameIdRef.current = gameId;
      previousEligibleRef.current = false;
      planViewAutoSetRef.current = false;
    }
    // #3038 forward gate: navigate to the plan surface on the rising edge of a
    // genuine review gate (Bugbot #3044 — fires whichever feeder signal lands
    // last). An auto-proceeding build never becomes eligible, so it never parks
    // on the plan doc and hides the live build-watch status row.
    if (
      shouldAutoNavigateToPlanReview({
        eligibleNow,
        previouslyEligible: previousEligibleRef.current,
      })
    ) {
      if (designWorkspaceEnabled) {
        bumpBuildPlanFocusNonce();
        setWorkspaceMode('design');
      } else {
        setWorkspaceMode('plan');
      }
      planViewAutoSetRef.current = true;
    }
    previousEligibleRef.current = eligibleNow;
    // setWorkspaceMode / bumpBuildPlanFocusNonce are stable actions; gameId +
    // eligibility (and the design-surface flag it branches on) re-run this gate.
    // gameId must be a dep so the per-game reset above runs even when eligibility
    // is unchanged across the switch (both games at a gate).
  }, [gameId, eligibleNow, designWorkspaceEnabled, newUiEnabled]);

  // #3038 return leg: once an AUTO-navigated plan review resolves into a running
  // build, return to the build surface (chat) so the live status row is visible
  // — the same #3038 complaint otherwise recurs through the manual-gate path
  // after the user clicks Start Building. Consume the auto-set flag once the
  // build is active (whether or not we navigated — the user may already have
  // left the plan doc) so a later MANUAL navigation to the plan is never yanked.
  useEffect(() => {
    if (!newUiEnabled) return;
    if (
      shouldReturnToBuildFromPlan({
        planViewWasAutoSet: planViewAutoSetRef.current,
        currentMode: workspaceMode,
        runStateKind,
      })
    ) {
      setWorkspaceMode('chat');
    }
    // Consume the auto-set flag once the review gate has RESOLVED — i.e. run-state
    // is no longer waiting-for-plan-review — not only on an active build. The
    // return-leg inputs are multi-source (the flag, run-state, mode), so a review
    // that resolves to a NON-build state (abandoned, back to drafting, a Phase-B
    // decision) must still clear the flag; otherwise it strands, and a later build
    // reached after a by-then-MANUAL navigation to the plan would yank it. The
    // gate re-opening re-arms the flag via the forward gate, so the refine loop
    // (waiting -> drafting -> waiting -> building) still returns correctly.
    if (planViewAutoSetRef.current && runStateKind !== 'waiting-for-plan-review') {
      planViewAutoSetRef.current = false;
    }
    // setWorkspaceMode is a stable action; workspaceMode + runStateKind drive it.
    // gameId is a dep so this re-evaluates on a switch render (after the forward
    // effect has reset planViewAutoSetRef) even if mode + run-state are unchanged.
  }, [gameId, newUiEnabled, workspaceMode, runStateKind]);
}
